import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  ADMIN_DATETIME_HOUR_OPTIONS,
  ADMIN_DATETIME_MINUTE_OPTIONS,
  ADMIN_DATETIME_TZ,
  type AdminDateTimeSelectParts,
  type Meridiem,
  buildAdminDateTimeIsoFromSelectParts,
  formatAdminDateTimePreview,
  formatAdminDateTimeSelectParts,
  normalizeMeridiem,
  parseAdminDateTime,
} from '../../utils/datetimeInput'

dayjs.extend(utc)
dayjs.extend(timezone)

interface AdminCentralDateTimeFieldProps {
  label: string
  value?: string | null
  onChange?: (value: string | null) => void
  min?: string | null
  max?: string | null
  allowClear?: boolean
  optional?: boolean
}

const AdminCentralDateTimeField = ({
  label,
  value,
  onChange,
  min,
  max,
  allowClear = true,
  optional = true,
}: AdminCentralDateTimeFieldProps) => {
  const [parts, setParts] = useState<AdminDateTimeSelectParts>(() => formatAdminDateTimeSelectParts(value))

  const minDayjs = useMemo(() => (min ? parseAdminDateTime(min) : null), [min])
  const maxDayjs = useMemo(() => (max ? parseAdminDateTime(max) : null), [max])

  useEffect(() => {
    setParts(formatAdminDateTimeSelectParts(value))
  }, [value])

  const preview = useMemo(() => {
    return formatAdminDateTimePreview(value) ?? formatAdminDateTimePreview(buildAdminDateTimeIsoFromSelectParts(parts))
  }, [value, parts])

  const emitParts = (nextParts: AdminDateTimeSelectParts) => {
    setParts(nextParts)

    if (!nextParts.date) {
      onChange?.(null)
      return
    }

    let iso = buildAdminDateTimeIsoFromSelectParts(nextParts)
    if (!iso) {
      return
    }

    let parsed = parseAdminDateTime(iso)
    if (!parsed) {
      return
    }

    if (minDayjs && parsed.isBefore(minDayjs)) {
      parsed = minDayjs
      iso = parsed.toISOString()
      setParts(formatAdminDateTimeSelectParts(iso))
    }

    if (maxDayjs && parsed.isAfter(maxDayjs)) {
      parsed = maxDayjs
      iso = parsed.toISOString()
      setParts(formatAdminDateTimeSelectParts(iso))
    }

    onChange?.(iso)
  }

  const handleDateChange = (date: string) => {
    emitParts({
      ...parts,
      date,
    })
  }

  const handleHourChange = (hour: string) => {
    emitParts({
      ...parts,
      hour,
    })
  }

  const handleMinuteChange = (minute: string) => {
    emitParts({
      ...parts,
      minute,
    })
  }

  const handleMeridiemChange = (meridiem: Meridiem) => {
    emitParts({
      ...parts,
      meridiem,
    })
  }

  const handleClear = () => {
    const cleared = formatAdminDateTimeSelectParts(null)
    setParts(cleared)
    onChange?.(null)
  }

  const labelText = optional ? `${label} (optional)` : label

  return (
    <div className="admin-central-datetime-field">
      <div className="admin-central-datetime-field__header">
        <label className="admin-central-datetime-field__label">{labelText}</label>
        {allowClear && value && (
          <button type="button" className="admin-central-datetime-field__clear" onClick={handleClear}>
            Clear
          </button>
        )}
      </div>

      <div className="admin-central-datetime-field__controls">
        <input
          type="date"
          className="admin-central-datetime-field__date"
          value={parts.date}
          min={min ? dayjs(min).tz(ADMIN_DATETIME_TZ).format('YYYY-MM-DD') : undefined}
          max={max ? dayjs(max).tz(ADMIN_DATETIME_TZ).format('YYYY-MM-DD') : undefined}
          onChange={event => handleDateChange(event.target.value)}
        />

        <div className="admin-central-datetime-field__time">
          <select
            className="admin-central-datetime-field__select"
            value={parts.hour}
            disabled={!parts.date}
            onChange={event => handleHourChange(event.target.value)}
            aria-label={`${label} hour`}
          >
            {ADMIN_DATETIME_HOUR_OPTIONS.map(hour => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>

          <span className="admin-central-datetime-field__separator">:</span>

          <select
            className="admin-central-datetime-field__select"
            value={parts.minute}
            disabled={!parts.date}
            onChange={event => handleMinuteChange(event.target.value)}
            aria-label={`${label} minute`}
          >
            {ADMIN_DATETIME_MINUTE_OPTIONS.map(minute => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>

          <select
            className="admin-central-datetime-field__select admin-central-datetime-field__meridiem"
            value={parts.meridiem}
            disabled={!parts.date}
            onChange={event => handleMeridiemChange(normalizeMeridiem(event.target.value))}
            aria-label={`${label} AM or PM`}
          >
            <option value="am">AM</option>
            <option value="pm">PM</option>
          </select>
        </div>
      </div>

      {preview ? (
        <p className="admin-central-datetime-field__preview">{preview}</p>
      ) : (
        <p className="admin-central-datetime-field__hint">Pick a date, then set the time in Central Time.</p>
      )}
    </div>
  )
}

export default AdminCentralDateTimeField
